import { useTranslation } from 'react-i18next';
import React, { memo, useCallback, useState, useEffect, useMemo } from 'react';
import cx from 'clsx';

import {
  login,
  getSpecialAvatarUrlByAddress,
  isSybilByUserDesc,
  openNewWindow,
  getOfficialPath,
} from 'utils';
import {
  useCurrentAddressInfo,
  useSwitch,
  useUserInfo,
  AvatarRes,
  useDeviceTypeIsMobile,
  useTheme,
} from 'hooks';
import { IconDefaultAvatar, IconDefaultAvatarDark } from '@/assets/icon/avatar';
import { AvatarModal } from './AvatarModal';
import style from './index.module.scss';
import { UserAvatar } from '../UserAvatar';
import produce from 'immer';
import { OfficialAccountAvatar } from '../OfficialAccountAvatar';
import HelperTooltip from '../HelperTooltip';
import { GroupAvatar } from '../GroupAvatar';

type AvatarProps = {
  desc?: UserDesc;
  url?: string;
  contentType?: AvatarRes['content_type'];
  address: string;
  size?: number;
  enablePrivate?: boolean;
  className?: string;
  rank?: number;
  handleClick?(): void;
  defaultAvatar?: string;
  defaultSybil?: string;
  defaultContract?: string;
};

export const AvatarEditable = memo(
  ({
    desc: _desc,
    address,
    size = 56,
    className,
    rank,
    handleClick,
    defaultAvatar,
    defaultSybil,
    defaultContract,
    ...rest
  }: AvatarProps) => {
    const { t } = useTranslation();

    // if pass url in props, Avatar won't request each user individual
    const [useUrl, url] = useMemo(() => ['url' in rest, rest.url], [rest]);
    const desc = useMemo(
      () =>
        _desc &&
        produce(_desc, draft => {
          if (draft.user.logo_thumbnail_url) {
            draft.user.logo_thumbnail_url = '';
          }
        }),
      [_desc],
    );
    const isMobile = useDeviceTypeIsMobile();

    const [avatarUrl, setAvatarUrl] = useState('');
    const { on, turnOn, turnOff } = useSwitch();
    const { data: addrInfo } = useUserInfo(useUrl ? undefined : address);
    const openOfficialProfileLink = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        openNewWindow(getOfficialPath(desc?.user?.official!), '_blank');
      },
      [desc?.user?.official],
    );
    const openGroupDetailLink = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        openNewWindow(`/group/${desc?.user?.group?.id}`, '_blank');
      },
      [desc?.user?.group?.id],
    );
    const specialAvatarUrl = useMemo(() => getSpecialAvatarUrlByAddress(address), [address]);
    const { isDark } = useTheme();
    useEffect(() => {
      const _url =
        specialAvatarUrl ||
        url ||
        addrInfo?.desc?.user?.logo_thumbnail_url ||
        addrInfo?.desc?.user?.logo_url ||
        '';

      setAvatarUrl(_url);
    }, [addrInfo, url, rest.contentType, address, specialAvatarUrl]);

    const { address: currentAddress, isVerified } = useCurrentAddressInfo();

    const handleEditClick = useCallback(() => {
      if (!isVerified) {
        // 应该是不存在这种情况了
        login();
        return;
      }
      turnOn();
    }, [turnOn, isVerified]);

    const showEdit =
      !isMobile &&
      address &&
      currentAddress === address &&
      !isSybilByUserDesc(desc) &&
      !desc?.user?.is_suicide;

    const isDefault = !desc && !(avatarUrl || specialAvatarUrl);

    return (
      <div className={style.container}>
        <div
          className={cx(
            style.avatarWrap,
            rank && rank <= 3 && [style.rank, style[`rank-${rank}`]],
            avatarUrl && desc?.user?.logo_is_nft && style.nftAvatar,
            desc && style.isDesc,
            // desc?.user?.logo_is_nft && rank && rank <= 3 && style.nftAvatar,
            isDefault && style.isDefault,
            desc?.user?.is_suicide && style.isSuicide,
            className,
          )}
          key={address}
          onClick={handleClick}
          style={{ width: size, height: size }}>
          <div className={style.avatar}>
            {desc ? (
              <UserAvatar
                data={desc}
                avatarPlaceholder={defaultAvatar}
                enableOfficialLogo={false}
                enableGroupAvatar={false}
                size={size}
              />
            ) : avatarUrl || specialAvatarUrl ? (
              <img src={avatarUrl} alt="" className={style.avatarImg} key={avatarUrl} />
            ) : (
              <img
                src={defaultAvatar || isDark ? IconDefaultAvatarDark : IconDefaultAvatar}
                alt=""
                key="defaultAvatar"
              />
            )}
            {showEdit ? (
              <div className={style.edit} onClick={handleEditClick}>
                <div className={style.editText}>{t('AvatarEditable.1a6ui')}</div>
              </div>
            ) : null}
            {on && address && (
              <AvatarModal
                address={address}
                onClose={turnOff}
                userInfo={addrInfo}
                onChange={setAvatarUrl}
              />
            )}
          </div>
        </div>
        {/* avatar 中的officialAvatar 会被showEdit时的clip-path挡住，暂时只想到这种方式解决 */}

        {!desc?.user?.is_suicide ? (
          <div className={style.logoAvatarList}>
            {desc?.user?.official && (
              <HelperTooltip
                placement="bottom"
                title={t('AvatarEditable.team', { name: desc?.user?.official?.name })}>
                <div>
                  <OfficialAccountAvatar
                    logoVisible={false}
                    size={32}
                    data={desc?.user?.official}
                    onClick={openOfficialProfileLink}
                  />
                </div>
              </HelperTooltip>
            )}
            {desc?.user?.group && (
              <HelperTooltip placement="bottom" title={desc?.user?.group?.name}>
                <div>
                  <GroupAvatar
                    logoVisible={false}
                    size={32}
                    data={desc?.user?.group}
                    onClick={openGroupDetailLink}
                  />
                </div>
              </HelperTooltip>
            )}
          </div>
        ) : null}
      </div>
    );
  },
);

export default AvatarEditable;
